/**
 * Wedding Books — Revenue & Conversions.
 *
 * Two revenue stories on one page, because the second one IS revenue: what the
 * couples paid, and how many of their guests then became LifeScribe members.
 * Splitting them into two pages would have meant a "conversions" page with
 * three numbers on it.
 *
 * THE RANGE COMES FROM THE SHARED FILTER, THE WIRE DOES NOT CHANGE. The pills
 * and the URL now speak the dashboard-wide `TimeRangeValue` vocabulary, but
 * this endpoint has always taken explicit `start`/`end` ISO instants and still
 * does — the window is resolved here and `bucketFor()` picks a bar width the
 * range can carry, because a year of daily bars is unreadable. The server
 * accepts both wire formats and prefers start/end, so nothing about the
 * measurement moved when the control did.
 *
 * DELTAS ARE THE SERVER'S. `prior_totals` and `prior_trend` are computed
 * against a window of exactly this length ending where this one begins; the
 * page never re-derives "previous", so the ghost bars and the KPI arrows are
 * always describing the same period.
 */

import React from 'react';
import { Box, Grid, Typography, Card, CardContent, Stack, Chip } from '@mui/material';
import { Paid as PaidIcon } from '@mui/icons-material';
import MetricCard from '../../common/MetricCard';
import EmptyState from '../../common/EmptyState';
import DataTable, { Column } from '../../common/DataTable';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import { describeRange, resolveRange, resolveRangeYmd, toApiToken } from '../../common/timeRange';
import WeddingKpiCard from '../WeddingKpiCard';
import RangeMetaCaption from '../RangeMetaCaption';
import RevenueTrendChart from './RevenueTrendChart';
import TierMixCard from './TierMixCard';
import VoiceInsightCard from './VoiceInsightCard';
import ClaimsCard from './ClaimsCard';
import RefundCaption from './RefundCaption';
import ConversionFunnelCard from '../overview/ConversionFunnelCard';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { ConversionsResponse, RevenueResponse, UtmRow } from '../weddingApi';
import { centsToUsdTile, num, percentage } from '../weddingFormat';

/** Daily bars past ~90 days is a smear; monthly past a year is too coarse. */
function bucketFor(start: Date, end: Date): 'day' | 'week' | 'month' {
  const days = (end.getTime() - start.getTime()) / 86400000;
  if (days <= 45) return 'day';
  if (days <= 200) return 'week';
  return 'month';
}

/** Percentage change against the prior period, or NULL when there is no base. */
function deltaPct(value: number, prev: number): number | null {
  if (!Number.isFinite(value) || !Number.isFinite(prev) || prev === 0) return null;
  return Math.round(((value - prev) / prev) * 1000) / 10;
}

const WeddingRevenuePage: React.FC = () => {
  const [range, setRange] = useTimeRangeParam({ type: '90d' });

  const [revenue, setRevenue] = React.useState<RevenueResponse | null>(null);
  const [conversions, setConversions] = React.useState<ConversionsResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [revenueError, setRevenueError] = React.useState<string | null>(null);
  const [conversionsError, setConversionsError] = React.useState<string | null>(null);

  const loadRevenue = React.useCallback(async () => {
    setRevenueError(null);
    // `from`/`to` are still resolved locally, but ONLY to choose the bucket —
    // the wire carries calendar dates. See the block comment on the wire below.
    const { from, to } = resolveRange(range);
    try {
      setRevenue(
        await weddingApi.getRevenue({
          /*
            THE SAME WIRE AS OVERVIEW AND MEMBERS — DATES, NOT INSTANTS.
            
            This used to send `start`/`end` as `from.toISOString()`, i.e. the
            operator's local midnight expressed in UTC. That takes /revenue's
            explicit-instant branch and bypasses resolveRange entirely, so for
            anyone off UTC the SAME PILL covered a different window here than on
            the pages beside it: on 30D in America/Denver, Overview measured
            Jul 23 00:00Z → Aug 21 23:59Z while Revenue measured Jul 23 06:00Z →
            Aug 22 05:59Z. Equal length, six hours out of step, no way to tell
            from either screen.

            Sending calendar dates puts this endpoint through the same whole-day
            resolution as the others, and has a second effect worth naming: the
            explicit branch returns `range_meta: null`, so Revenue was the one
            windowed screen that could not disclose its own window. Now it can.
          */
          range: toApiToken(range),
          ...resolveRangeYmd(range),
          bucket: bucketFor(from, to),
        })
      );
    } catch (e) {
      setRevenueError(e instanceof WeddingApiError ? e.message : 'Could not load revenue.');
      setRevenue(null);
    }
  }, [range]);

  const loadConversions = React.useCallback(async () => {
    setConversionsError(null);
    try {
      setConversions(await weddingApi.getConversions());
    } catch (e) {
      setConversionsError(e instanceof WeddingApiError ? e.message : 'Could not load conversions.');
      setConversions(null);
    }
  }, []);

  const loadAll = React.useCallback(() => {
    setLoading(true);
    return Promise.all([loadRevenue(), loadConversions()]).finally(() => setLoading(false));
  }, [loadRevenue, loadConversions]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadRevenue(), loadConversions()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadRevenue, loadConversions]);

  const rangeLabel = describeRange(range);

  // ATTACH AND UPGRADE ARE SCOPED TO THE RANGE when the server offers the
  // range-scoped figures, and the card says which cohort it counted. `attach`
  // stays all-time on purpose (a book bought in January and upgraded in March
  // is attached, in any window), so mixing the two silently would be the bug.
  const attachInRange = revenue?.attach_in_range;
  const attachSource = attachInRange || revenue?.attach;
  const attachRate = percentage(attachSource?.books_with_extras, attachSource?.books_paid);
  const upgradeRate = percentage(attachSource?.books_upgraded, attachSource?.books_paid);
  const attachScopeNote = attachInRange ? `paid in ${rangeLabel}` : 'paid, all time';

  const revenueDelta = deltaPct(
    num(revenue?.totals?.cents),
    num(revenue?.prior_totals?.cents)
  );
  const hasPrior = revenue?.prior_totals !== undefined;

  const utmColumns: Column<UtmRow>[] = [
    { id: 'source', label: 'Source' },
    { id: 'campaign', label: 'Campaign' },
    { id: 'touched', label: 'Saw it', align: 'right' },
    { id: 'clicked', label: 'Clicked', align: 'right' },
    { id: 'converted', label: 'Joined', align: 'right' },
    {
      id: 'rate',
      label: 'Rate',
      align: 'right',
      render: (row) => `${percentage(row.converted, row.touched)}%`,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <PaidIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Revenue &amp; Conversions
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          onRefresh={loadAll}
          refreshing={loading}
        />
      </Box>
      {/*
        The window, described by the SERVER — the same caption Overview carries.
        RangeMetaCaption's own header says all three windowed screens ship this
        block; for a while only one of them rendered it, so the timezone note
        the server computes reached a third of the pages it was written for.
      */}
      <RangeMetaCaption meta={revenue?.range_meta} sx={{ mb: 1 }} />
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        What couples paid over {rangeLabel}, and how many of their guests went on to become
        LifeScribe members.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="Revenue in range"
            value={centsToUsdTile(revenue?.totals?.cents)}
            subtitle={`${revenue?.totals?.purchases || 0} purchases across ${revenue?.totals?.books || 0} books`}
            deltaPct={hasPrior ? revenueDelta : undefined}
            showDelta={hasPrior}
            tone="primary"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard
            title="Extras attach rate"
            value={`${attachRate}%`}
            subtitle={`${attachSource?.books_with_extras || 0} of ${attachSource?.books_paid || 0} books ${attachScopeNote} bought extra contributors`}
            color="content"
            icon="userGroup"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard
            title="Upgrade rate"
            value={`${upgradeRate}%`}
            subtitle={`${attachSource?.books_upgraded || 0} books moved up a tier`}
            color="costs"
            icon="trending_up"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <MetricCard
            title="Guests who joined"
            value={conversions?.funnel?.converted || 0}
            subtitle={`${percentage(conversions?.funnel?.converted, conversions?.funnel?.touched)}% of guests who saw the cross-sell`}
            color="users"
            icon="favorite"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/*
        E2 — the tiles above are GROSS and keep their meaning. Net, the refunded
        total and the all-time rate live here, under them, where each can carry
        its own basis. `refunds` is optional on the type: a backend that predates
        the key renders nothing at all rather than zeros it never measured.
      */}
      <RefundCaption refunds={revenue?.refunds} rangeLabel={rangeLabel} />

      {/*
        E1 — unclaimed purchases. All time on purpose (it fetches its own data
        and ignores the pill above), because "money taken and never redeemed" is
        not a windowed question.
      */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ClaimsCard />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          {revenueError ? (
            <EmptyState
              icon="error"
              title="Could not load revenue"
              description={revenueError}
              action={{ label: 'Retry', onClick: loadRevenue, variant: 'contained' }}
            />
          ) : (
            <RevenueTrendChart
              points={revenue?.trend || []}
              bucket={revenue?.bucket || 'day'}
              priorPoints={revenue?.prior_trend}
              bestBucket={revenue?.best_bucket}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <TierMixCard rows={revenue?.tier_mix || []} />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <ConversionFunnelCard
            funnel={conversions?.funnel || { touched: 0, clicked: 0, converted: 0 }}
            title="Guest → LifeScribe"
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 8 }}>
          {conversionsError ? (
            <EmptyState
              icon="error"
              title="Could not load conversions"
              description={conversionsError}
              action={{ label: 'Retry', onClick: loadConversions, variant: 'contained' }}
            />
          ) : (
            <Card sx={{ borderRadius: '16px', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Where the conversions came from
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  UTM attribution recorded when a guest reached the cross-sell. All time, not
                  the selected range. Each row counts people within its own source/campaign pair, so somebody whose conversion rows carry two different sources appears in both — these rows attribute channels, they do not add up to the funnel.
                </Typography>
                <DataTable
                  columns={utmColumns}
                  data={conversions?.by_utm || []}
                  emptyMessage="No guest has reached the cross-sell yet."
                />
                {(conversions?.recent || []).length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {(conversions?.recent || []).slice(0, 8).map((c) => (
                      <Chip
                        key={c.id}
                        size="small"
                        label={`${c.first_name || 'A guest'} · ${c.wedding_code}`}
                      />
                    ))}
                  </Stack>
                )}
                <VoiceInsightCard insight={revenue?.voice_insight} />
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
        Amounts are Stripe-settled totals in USD ({num(revenue?.totals?.purchases)} paid purchases
        in the selected window).
        {revenue?.prior_range
          && ` The ghosted bars and the delta compare against ${new Date(revenue.prior_range.start)
            .toLocaleDateString()} – ${new Date(revenue.prior_range.end).toLocaleDateString()}.`}
      </Typography>
    </Box>
  );
};

export default WeddingRevenuePage;
